import { ColumnDef } from '@tanstack/react-table';
import { EllipsisVertical } from 'lucide-react';
import { DataTableColumnHeader, StatusChip, UserNameCell } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { AttendanceCorrection } from '../../types/manage-attendance.types';
import {
  formatAttendanceDay,
  formatShiftEnd,
  formatTimeOfDay,
} from '@/modules/time-attendance/attendance/services/attendance-rules';

const CORRECTION_STATUSES = ['Pending', 'Approved', 'Rejected'];

interface CreateCorrectionRequestsTableColumnsProps {
  t: (key: string) => string;
  /** Opens the confirmation of the decision picked from the row's menu. */
  onReview: (correction: AttendanceCorrection, action: 'Approved' | 'Rejected') => void;
  /** Undefined hides the waive-fine action entirely — the caller only passes it when the
   * signed-in reviewer holds ATTENDANCE.WAIVE_CORRECTION_FINE. */
  onWaiveFine?: (correction: AttendanceCorrection) => void;
  canApprove?: boolean;
  /** The signed-in reviewer's own id — a reviewer cannot approve or reject their own request,
   * so the Review action is hidden on their own row (the one rule the legacy backend actually
   * enforces server-side; everything else about "who can review" is a plain permission check). */
  reviewerId?: string;
  /** Adds the selection column for bulk approve / reject (holders of REVIEW_CORRECTION). */
  selectable?: boolean;
  /** The employee's name and email (the subtitle); corrections carry only the UserId. */
  nameOf?: (userId: string) => string | undefined;
  emailOf?: (userId: string) => string | undefined;
  /** The departments the user may see (`useDepartmentScope().departments`), for the Department
   * column's dropdown filter. */
  departments?: readonly { ItemId: string; Title: string }[];
}

/** A request the signed-in reviewer may decide: still pending and not their own. */
export const isReviewable = (correction: AttendanceCorrection, reviewerId: string) =>
  correction.Status === 'Pending' && correction.UserId !== reviewerId;

export const createCorrectionRequestsTableColumns = ({
  t,
  onReview,
  onWaiveFine,
  canApprove = true,
  reviewerId = '',
  selectable = false,
  nameOf,
  emailOf,
  departments = [],
}: CreateCorrectionRequestsTableColumnsProps): ColumnDef<AttendanceCorrection>[] => [
  ...(selectable
    ? [
        {
          // AdvanceDataTable draws each row's checkbox for the column with this id.
          id: 'select',
          enableSorting: false,
          enableHiding: false,
          header: ({ table }) => {
            const reviewable = table
              .getRowModel()
              .rows.filter((row) => isReviewable(row.original, reviewerId));
            const allSelected =
              reviewable.length > 0 && reviewable.every((row) => row.getIsSelected());
            return (
              <Checkbox
                checked={allSelected}
                disabled={reviewable.length === 0}
                onCheckedChange={(value) =>
                  reviewable.forEach((row) => row.toggleSelected(Boolean(value)))
                }
                aria-label={t('SELECT_ALL')}
                className="border-muted-foreground data-[state=checked]:border-none border-2"
              />
            );
          },
        } as ColumnDef<AttendanceCorrection>,
      ]
    : []),
  {
    // Looked up from UserId: no field to sort or filter on (the toolbar's keyword finds employees).
    id: 'EmployeeName',
    enableSorting: false,
    // The card has the name as its title and the email in its body instead.
    meta: { mobile: 'hidden' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => nameOf?.(row.UserId) ?? '',
    cell: ({ row }) => (
      <UserNameCell name={nameOf?.(row.original.UserId)} email={emailOf?.(row.original.UserId)} />
    ),
  },
  {
    id: 'CardEmployeeName',
    enableSorting: false,
    meta: { mobile: 'title', cardOnly: true },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => nameOf?.(row.UserId) || '-',
  },
  {
    // Card body, two per line: Date | Status, Email | Department, Start | End; the fine in the footer.
    id: 'CardEmail',
    enableSorting: false,
    meta: { mobile: 'body', mobileOrder: 2, cardOnly: true },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
    accessorFn: (row) => emailOf?.(row.UserId) || '-',
  },
  {
    // Filtered by the department name stored on the correction; the filter is a dropdown of the
    // departments the user may see, and the list's department scope (by id) still applies on top.
    id: 'DepartmentName',
    meta: {
      mobile: 'body',
      mobileOrder: 3,
      filter: {
        type: 'options',
        dropdown: true,
        options: departments.map((department) => ({
          value: department.Title,
          label: department.Title,
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName ?? '-',
  },
  {
    id: 'CorrectionDate',
    meta: { filter: { type: 'date' }, mobileOrder: 0 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CORRECTION_DATE')} />,
    accessorFn: (row) => row.CorrectionDate,
    cell: ({ row }) => formatAttendanceDay(row.original.CorrectionDate),
  },
  {
    // Time of day on the organization's clock, over a picked day range (see `ColumnFilterConfig`).
    id: 'StartTimeUtc',
    meta: { filter: { type: 'time', withDays: true }, mobileOrder: 4 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CLOCK_START_TIME')} />,
    accessorFn: (row) => formatTimeOfDay(row.StartTimeUtc, false, row.Timezone),
  },
  {
    id: 'EndTimeUtc',
    meta: { filter: { type: 'time', withDays: true }, mobileOrder: 5 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CLOCK_END_TIME')} />,
    accessorFn: (row) => formatShiftEnd(row.StartTimeUtc, row.EndTimeUtc, false, row.Timezone),
  },
  {
    // No column filter: the toolbar has a status filter.
    id: 'Status',
    meta: {
      // The card's top right holds the actions menu.
      mobile: 'body',
      mobileOrder: 1,
      filter: {
        type: 'options',
        options: CORRECTION_STATUSES.map((status) => ({
          value: status,
          label: t(status.toUpperCase()),
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status,
    cell: ({ row }) => (
      <StatusChip status={row.original.Status}>{t(row.original.Status.toUpperCase())}</StatusChip>
    ),
  },
  {
    id: 'Fine',
    meta: { filter: { type: 'number' }, mobile: 'footer' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('FINE')} />,
    // Charged on approval: an approved row shows its fine; a pending row the fine its approval would
    // charge (marked so); a rejected row none.
    accessorFn: (row) => (row.Status !== 'Rejected' && row.Fine ? row.Fine.toFixed(2) : '-'),
    cell: ({ row }) => {
      const { Status, Fine } = row.original;
      if (Status === 'Rejected' || !Fine) return '-';
      if (Status !== 'Pending') return Fine.toFixed(2);
      return (
        <span className="text-muted-foreground">
          {Fine.toFixed(2)} ({t('FINE_ON_APPROVAL')})
        </span>
      );
    },
  },
  ...(canApprove
    ? [
        {
          id: 'actions',
          // Card: top right, the status moves into the body.
          meta: { mobile: 'badge' as const },
          header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }: { row: { original: AttendanceCorrection } }) => {
            const correction = row.original;
            // Each item opens its confirmation; nothing happens from the menu itself.
            const canReviewRow = isReviewable(correction, reviewerId);
            const canWaiveRow =
              onWaiveFine && correction.Status === 'Approved' && (correction.Fine ?? 0) > 0;
            if (!canReviewRow && !canWaiveRow) return null;
            // Every action is in the row's menu; no icon quick actions (agreed 2026-11-02).
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={t('ACTION')}>
                    <EllipsisVertical className="size-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canReviewRow && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onSelect={() => onReview(correction, 'Approved')}
                    >
                      {t('APPROVE')}
                    </DropdownMenuItem>
                  )}
                  {canReviewRow && (
                    <DropdownMenuItem
                      className="cursor-pointer text-destructive"
                      onSelect={() => onReview(correction, 'Rejected')}
                    >
                      {t('REJECT')}
                    </DropdownMenuItem>
                  )}
                  {canWaiveRow && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onSelect={() => onWaiveFine?.(correction)}
                    >
                      {t('WAIVE_FINE')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          },
        },
      ]
    : []),
];
